<template>
  <component
      ref="inputRef"
      :is="inputElement"
      :type="inputType"
      :placeholder="parsedPlaceholder"
      :name="parsedName"
      :value="inputValue"
      class="form-control"
      :autocomplete="autocompleteValue"
      spellcheck="false"
      :disabled="disabled"
      :class="[inputClass, {
        'is-invalid': error,
        'form-control-sm': small,
        'disabled': disabled,
      }]"
      :style="style"
      @keyup="onKeyUp"
      @keydown="onKeyDown"
      @input="onChange"
      @focus="onFocus"
      @dragover="onDragOver"
      @drop="onDrop"
      @dragleave="onDragLeave"
  />
</template>
<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import FormControlProps from "@/js/Elements/Form/FormControlProps.js";
import {useFormControl, useFormControlParser} from "@/js/Elements/Form/FormControl.js";
import {hideAllPoppers} from "floating-vue";

const emit = defineEmits(['change', 'focus', 'update:modelValue', 'enter', 'dragover', 'dragleave']);
const props = defineProps({
  ...FormControlProps,
  showPassword: Boolean,
});
const {
  error,
  inputValue
} = useFormControl(props, emit);
const { parsedPlaceholder, parsedName } = useFormControlParser(props);
const inputElement = computed(() => {
  switch (props.type) {
    case 'text':
    case 'number':
    case 'password':
    case 'colorpicker':
      return 'input';
    case 'textarea':
      return 'textarea';
  }
  return null;
});
const inputType = computed(() => {
  return props.showPassword ? 'text' : props.type;
})
// Respect an explicit autocomplete string (e.g. "new-password" to stop browser
// credential autofill); default to "off" as before.
const autocompleteValue = computed(() => {
  return typeof props.autocomplete === 'string' ? props.autocomplete : 'off';
});
const style = reactive({
  resize: props.resizable ? null : 'none',
});
const inputRef = ref();
const shiftDown = ref(false);

defineExpose({
  setValue(value) {
    inputValue.value = value;
  },
  focus() {
    inputRef.value.focus();
  }
})

let visibilityObserver = null;

onMounted(() => {
  if (props.type === 'textarea') {
    if (props.autoResize) {
      watch(() => props.modelValue, resizeWithDelay);
      style.overflow = 'hidden';
      style.resize = 'none';
      setTimeout(resize, 10);

      visibilityObserver = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) {
          resize();
        }
      });
      if (inputRef.value) visibilityObserver.observe(inputRef.value);
    }
  }
});

onBeforeUnmount(() => {
  visibilityObserver?.disconnect();
});

function onFocus(e) {
  const parent = inputRef.value.closest('.v-popper__popper');
  if (!parent){
    hideAllPoppers();
  }
  emit('focus', e);
}

function onChange(e) {
  let value = e.target.value;

  // Optional digit-only restriction (e.g. IMEI). Strip non-digits and force the
  // DOM input back in sync — when the cleaned value equals the current model
  // Vue skips the re-render, so the stripped character would otherwise stay
  // visible in the field.
  if (props.numeric) {
    const clean = value.replace(/\D/g, '');
    if (clean !== value) {
      value = clean;
      e.target.value = clean;
    }
  }

  emit('change', value);

  if (props.autoResize) {
    resize();
  }
}

function resizeWithDelay(e) {
  setTimeout(resize.bind(null, e), 1);
}

function resize(e) {
  style.height = 'auto';
  style.maxHeight = props.modelValue ? 'unset' : null;
  nextTick(() => {
    if (!inputRef.value) return;
    style.height = props.modelValue ? `${inputRef.value.scrollHeight}px` : null;
  });
}

function onKeyUp(e) {
  if (props.type !== 'textarea') return;
  if (e.keyCode === 16) {
    shiftDown.value = false;
  }

  if (e.keyCode === 13 && !shiftDown.value) {
    emit('enter');
  }
}

function onKeyDown(e) {
  if (props.type !== 'textarea') return;
  if (e.keyCode === 16) {
    shiftDown.value = true;
  }
}

function onDragOver(e) {
  if (!props.dropable) return;
  e.preventDefault();
  emit('dragover');
}

function onDragLeave(e) {
  if (!props.dropable) return;
  emit('dragleave');
}

function onDrop(e) {
  if (!props.dropable) return;
  e.preventDefault();

  emit('dragleave');
  const file = e.dataTransfer.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (event) => {
    try {
      const json = JSON.parse(event.target.result); // parse
      inputValue.value = JSON.stringify(json, null, 2); // format nicely
    } catch (err) {
      inputValue.value = null;
    }
  };

  reader.readAsText(file);
}
</script>